import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingController, ModalController, PopoverController } from '@ionic/angular';
import { AlertMessager } from 'src/libs/alertMessager';
import { AccountsGroup } from 'src/models/account-group';
import { Account } from 'src/models/account.model';
import { NotificationType } from 'src/models/custom-notification';
import { Organization } from 'src/models/organization';
import { Rodeo } from 'src/models/rodeo';
import { RodeoEvent } from 'src/models/rodeoEvent';
import { RodeoEventSchedule } from 'src/models/rodeoEventSchedule';
import { AuthenticateService } from 'src/services/authenticate.service';

@Component({
  selector: 'app-custom-notification',
  templateUrl: './custom-notification.component.html',
  styleUrls: ['./custom-notification.component.scss'],
})
export class CustomNotificationComponent implements OnInit {
  @Input() accounts: Account[];
  @Input() organization: Organization;
  @Input() rodeo: Rodeo;
  @Input() accountsGroups: AccountsGroup[];
  @Input() displayedAsPopover: boolean = false;

  isLoading = false;

  currentUser: Account;

  rodeoEvents: RodeoEvent[] = [];
  schedules: RodeoEventSchedule[] = [];
  schedulesNameMap: Map<string, number[]> = new Map();

  notificationTypes = NotificationType;
  notificationType: NotificationType;
  notificationForm: FormGroup;

  previousFormValues: any;

  selectedSchedules: string[] = [];
  selectedRodeoEventsMap: Map<number, RodeoEvent> = new Map();
  selectedAccountsMap: Map<number, { id: number, name: string, account: Account }> = new Map();

  accountId: string; // TODO: Push Notifications (defaulted to current user id)
  emailToo: boolean; // TODO: Text Notifications
  testNumber: string; // TODO: Text Notifications

  get scheduleNameOptions() {
    return Array.from(this.schedulesNameMap.keys());
  }

  get selectedRodeoEvents() {
    return Array.from(this.selectedRodeoEventsMap.values());
  }

  get selectedAccounts() {
    return Array.from(this.selectedAccountsMap.values());
  }

  get selectedAccountsEmails() {
    const selectedAccounts = Array.from(this.selectedAccountsMap.values());
    return selectedAccounts.length > 0 ? selectedAccounts
      .filter(x => x.account.emailAddress)
      .map(x => x.account.emailAddress) : [];
  }
    
  constructor(
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private fb: FormBuilder,
    private authService: AuthenticateService,
    private alertMessager: AlertMessager
  ) { }

  ngOnInit() {
    this.isLoading = true;

    this.currentUser = this.authService.currentUser.value as Account;
    console.log('current user', this.currentUser);
    console.log('groups', this.accountsGroups);

    // TODO: (After implementing text & push) 
    // Remove setting notification type and initForm: Allow the user to pick the type of form before initing the form
    this.notificationType = NotificationType.Email;
    this.initForm();

    if (this.rodeo) {
      this.notificationForm.get('rodeos').patchValue([this.rodeo.id]);

      this.rodeo.rodeoEvents.forEach((rodeoEvent) => {
        if (this.selectedRodeoEventsMap.size <= 0) {
          this.selectedRodeoEventsMap.set(rodeoEvent.id, rodeoEvent);
        }

        this.rodeoEvents.push(rodeoEvent);

        rodeoEvent.schedules.forEach((schedule) => { 
          this.schedules.push(schedule);

          if (!this.schedulesNameMap.has(schedule.name)) {
            this.schedulesNameMap.set(schedule.name, []);
          }

          this.schedulesNameMap.get(schedule.name).push(schedule.id);
        });
      });
    }

    if (this.accountsGroups) {
      this.notificationForm.get('accountsGroups').patchValue(this.accountsGroups.map(x => x.id));
    }

    // // TODO: Do we ever need to allow for search and manipulation of WHO receives the notification being sent?
    if (this.accounts) {
      const emails = this.accounts
        .filter(x => x.emailAddress).map((x) => x.emailAddress);
      
      for (const account of this.accounts) {
        this.selectedAccountsMap.set(account.id, {
          id: account.id,
          name: `${account.firstName} ${account.lastName} - ${account.emailAddress}`,
          account
        });
      }

      this.notificationForm.get('ccEmails').patchValue(emails);
    }

    this.isLoading = false;
  }

  cancel() {
    if (this.displayedAsPopover) {
      this.popoverCtrl.dismiss();
    } else {
      this.modalCtrl.dismiss();
    }
  }

  async send() {
    if (this.notificationForm.invalid) {
      // TODO: Add validation 
      this.alertMessager.showToast(undefined, 'Please make sure all fields are filled out, then try again.', 'danger');
      return;
    }
    
    switch (this.notificationType) {
      case NotificationType.Email:
        this.emitData(NotificationType.Email, this.notificationForm.value);
        break;
      case NotificationType.Push:
        // TODO: CURRENTLY SENDING TO SELF (errors out in API, but acts as if it is successful)
        this.emitData(NotificationType.Push, this.notificationForm.value, undefined, undefined, `${this.currentUser.id}`);
        break;
      case NotificationType.Text:
        this.emitData(NotificationType.Text, this.notificationForm.value, this.emailToo, this.testNumber, undefined);
        break;
      default:
        throw new Error('Invalid notification type');
    }
  }

  private emitData(notificationType: NotificationType, formValue: any, emailToo?: boolean, testNumber?: string, accountId?: string) {
    // Email: formValue
    // Text: formValue, emailToo, testNumber
    // Push: formValue, accountId
    const data = {
      notificationType,
      formValue,
      emailToo,
      testNumber,
      accountId
    };

    if (this.displayedAsPopover) {
      this.popoverCtrl.dismiss(data);
    } else {
      this.modalCtrl.dismiss(data);
    }
  }

  accountsChanged(event: any) {
    console.log('accounts changed', event);
    this.selectedAccountsMap.clear();

    if (event.length > 0) {
      event.forEach((x) => {
        this.selectedAccountsMap
          .set(x, {
            id: x.id,
            name: `${x.firstName} ${x.lastName} - ${x.emailAddress}`,
            account: x.account
          });
      });
  
      this.notificationForm
        .get('ccEmails')
        .patchValue(this.selectedAccounts.filter(x => x.account.emailAddress).map(x => x.account.emailAddress));
    } else {
      this.notificationForm.get('ccEmails').patchValue([]);
    }
    
    console.log('updated emails', this.notificationForm.get('ccEmails')?.value);
  }

  classesChanged(event: any) {
    this.selectedRodeoEventsMap.clear();

    if (event.length > 0) {
      event.forEach((x) => {
        this.selectedRodeoEventsMap.set(x.id, x);
      });
  
      this.notificationForm.get('rodeoEvents').patchValue(this.selectedRodeoEvents.map(x => x.id));
    } else {
      this.notificationForm.get('rodeoEvents').patchValue([]);
    }
  }

  schedulesChanged(event: any) {
    console.log('schedulesChanged', event);
  }

  updateSelectedType(event: any) {
    const newType = event.detail.value;
    this.notificationType = newType;

    if (this.notificationForm) {
      this.initForm(this.notificationForm.value);
    } else {
      this.initForm();
    }
  }

  private initForm(item?: any) {
    // NOTE: Item = opposite formValue (check updateSelectedType())
    if (item) {
      this.notificationForm = undefined;
    }

    // Common fields for all notification types
    const commonFields = {
      disciplines: [item?.disciplines || []],
      rodeoEvents: [item?.rodeoEvents || []],
      schedules: [item?.schedules || []],
      rodeos: [item?.rodeos || []],
      accountsGroups: [item?.accountsGroups || []],
    };

    // Shared fields for Push and Text notifications
    const pushAndTextFields = {
      personalize: [true],
      title: [item?.subject, Validators.required],
      body: [item?.body, Validators.required],
      imgUrls: [[]], // TODO:
      females: [true],
      males: [true],
    };

    // Notification type-specific fields
    const notificationTypeFields = {
      [NotificationType.Email]: {
          from: [this.currentUser.emailAddress, Validators.required],
          sendTo: [undefined], // Handled in API
          ccEmails: [this.selectedAccountsEmails],
          subject: [item?.title, Validators.required],
          body: [item?.body, Validators.required],
      },
      [NotificationType.Push]: pushAndTextFields,
      [NotificationType.Text]: pushAndTextFields,
    };

    // Combine common and specific fields based on notification type
    this.notificationForm = this.fb.group({
        ...commonFields,
        ...(notificationTypeFields[this.notificationType] || {})
    });
  }

}
